import React from 'react';
import classnames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Types
import { BaInfoStatus } from 'Shared/types/BaInfo.types';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';
import { useUser } from 'Shared/utils/hooks/use-user';
import { useBaProfile } from 'Shared/utils/hooks/use-ba-profile';
import { useOrganization } from 'Shared/utils/hooks/use-organization';
import { useBaProfileDrawer } from 'Shared/utils/hooks/use-ba-profile-drawer';

// Components
import { WpIcon } from 'Wpds/components/icon';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import UserBaProfileSnapshot from 'Shared/components/UserBaProfileSnapshot/UserBaProfileSnapshot';
import BaProfileDisplayHeader from 'Shared/components/UserBaProfileSnapshot/_partials/BaProfileDisplayHeader';

// Styles
import './WorkspaceUserBaSnapshot.scss';

type Props = {
  view?: 'side-rail' | 'on-top';
};

const WorkspaceUserBaSnapshot = ({ view = 'side-rail' }: Props) => {
  const { slug: currentUserSlug } = useCurrentUser();
  const { workspace } = useWorkspace();
  const { featureFlagShowRelationshipGuideInWorkspace } = useFlags();

  const userSlug = workspace.isPersonal
    ? currentUserSlug
    : workspace.users.find(({ slug }) => slug !== currentUserSlug)?.slug;

  const { user, isPiUser } = useUser({ slug: userSlug });
  const { handleOpenBaDrawer } = useBaProfileDrawer({ user });

  const { isPiDemoAccount } = useOrganization();
  const isPiUserOrPiDemoAccount = isPiUser || isPiDemoAccount;

  const { isFetching, hasPendingBa, status } = useBaProfile({ user });

  const baseClassName = 'WorkspaceUserBaSnapshot';
  const classes = classnames(baseClassName, `${baseClassName}--${view}`);

  if (!user || !isPiUserOrPiDemoAccount) return null;

  if (workspace.isOneOnOne && isFetching) return null;

  if (workspace.isOneOnOne && status === BaInfoStatus.BA_NOT_FOUND && !featureFlagShowRelationshipGuideInWorkspace)
    return null;

  if (workspace.isOneOnOne && hasPendingBa && !featureFlagShowRelationshipGuideInWorkspace) return null;

  // Do not show on mobile if a BA does not exist for the user
  if (view === 'on-top' && (status === BaInfoStatus.BA_NOT_FOUND || hasPendingBa)) return null;

  if (view === 'on-top')
    return (
      <>
        <WpContainer className={classes} onClick={handleOpenBaDrawer}>
          <WpContainerBody padding="05" className={`${baseClassName}__mobile-wrapper`}>
            <BaProfileDisplayHeader user={user} />
            <WpIcon name="angle-right" size="lg" />
          </WpContainerBody>
        </WpContainer>
      </>
    );

  return (
    <div className={classes}>
      {workspace.isPersonal ? (
        <UserBaProfileSnapshot
          user={user}
          showViewInDrawerButton={true}
          showLearnMoreLink={false}
          showDownloadReportButton={false}
        />
      ) : (
        <UserBaProfileSnapshot user={user} showConversationTips showBaDetails={false} showViewInDrawerButton={true} />
      )}
    </div>
  );
};

export default WorkspaceUserBaSnapshot;
